import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuShieldCheck, LuUserCog } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { db } from '../../../config/firebaseConfig';
import { CARGO_ACCESS_FIELD } from '../../../utils/companyShipments';
import { EmptyState, HelpNote, LoadingRows, SearchField } from '../../ProfilePage/components/ProfileUi';
import useCargoText from './useCargoText';

const isActive = (employee) => employee.Active === 1 || employee.Active === '1';
const isAdmin = (employee) =>
  Array.isArray(employee.authorization) &&
  (employee.authorization.includes('admin') || employee.authorization.includes('Kısıtlı Admin'));
const fullName = (employee) => `${employee.eName || ''} ${employee.eSurname || ''}`.trim();

const initials = (employee) =>
  (fullName(employee) || employee.eMail || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toLocaleUpperCase('tr-TR'))
    .join('');

/**
 * Profilden kargo gönderme/alma özelliğini kullanabilecek çalışanların seçimi.
 * Her anahtar değişikliği Employees belgesine anında yazılır.
 */
const CargoAccessModal = ({ show, onHide }) => {
  const { t, language, label } = useCargoText();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (!show) return undefined;
    setLoading(true);
    return onSnapshot(
      collection(db, 'Employees'),
      (snapshot) => {
        const rows = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter(isActive)
          .sort((a, b) => fullName(a).localeCompare(fullName(b), 'tr'));
        setEmployees(rows);
        setLoading(false);
      },
      (error) => {
        console.error('Çalışanlar yüklenemedi:', error);
        toast.error(label('toast.staffError'));
        setLoading(false);
      }
    );
    // label yalnızca dil değişince değişir; aboneliği yeniden kurmaya gerek yok
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const visible = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('tr-TR');
    if (!needle) return employees;
    return employees.filter((employee) =>
      `${fullName(employee)} ${employee.eMail || ''}`.toLocaleLowerCase('tr-TR').includes(needle)
    );
  }, [employees, search]);

  const grantedCount = employees.filter((employee) => employee[CARGO_ACCESS_FIELD] === true).length;

  const toggle = async (employee) => {
    const next = employee[CARGO_ACCESS_FIELD] !== true;
    setSavingId(employee.id);
    try {
      await updateDoc(doc(db, 'Employees', employee.id), { [CARGO_ACCESS_FIELD]: next });
      toast.success(label(next ? 'toast.accessGranted' : 'toast.accessRevoked', { name: fullName(employee) }));
    } catch (error) {
      console.error('Kargo yetkisi güncellenemedi:', error);
      toast.error(label('toast.accessError'));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Modal
      isOpen={Boolean(show)}
      onClose={onHide}
      size="md"
      title={label('access.title')}
      subtitle={label('access.subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-violet-400 tw-to-indigo-600 tw-shadow-violet-500/30" size="xs">
          <LuUserCog aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
    >
      <div className="tw-space-y-3">
        <HelpNote>{label('access.help')}</HelpNote>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <Badge tone="accent">{label('access.granted', { count: grantedCount })}</Badge>
          <span className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('access.saved')}</span>
        </div>
        <SearchField value={search} onChange={setSearch} placeholder={label('access.search')} />

        {loading ? (
          <LoadingRows count={5} height="tw-h-14" label={label('access.loading')} />
        ) : visible.length === 0 ? (
          <EmptyState compact title={label('access.empty')} />
        ) : (
          <ul className="tw-max-h-[52vh] tw-space-y-1 tw-overflow-y-auto tw-pr-1 glass-scrollbar">
            {visible.map((employee) => {
              const admin = isAdmin(employee);
              const granted = admin || employee[CARGO_ACCESS_FIELD] === true;
              const name = fullName(employee) || employee.eMail;
              return (
                <li key={employee.id} className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-px-2.5 tw-py-2 tw-transition-colors hover:tw-bg-slate-900/[0.03] dark:hover:tw-bg-white/[0.04]">
                  <span aria-hidden="true" className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-slate-900/[0.05] tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-bg-white/[0.07] dark:tw-text-slate-300">
                    {initials(employee)}
                  </span>
                  <span className="tw-min-w-0 tw-flex-1">
                    <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{name}</span>
                    <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{employee.eMail}</span>
                  </span>
                  {admin ? (
                    <span title={label('access.adminHint')}>
                      <Badge tone="info">
                        <LuShieldCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                        {label('access.admin')}
                      </Badge>
                    </span>
                  ) : (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={granted}
                      aria-label={label('access.toggle', { name: fullName(employee) })}
                      onClick={() => toggle(employee)}
                      disabled={savingId === employee.id}
                      className={`tw-relative tw-h-6 tw-w-11 tw-shrink-0 tw-rounded-full tw-transition-colors tw-duration-200 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 focus-visible:tw-ring-offset-2 disabled:tw-cursor-wait disabled:tw-opacity-60 dark:focus-visible:tw-ring-offset-[#1f232b] ${
                        granted ? 'tw-bg-teal-500' : 'tw-bg-slate-300 dark:tw-bg-white/15'
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`tw-absolute tw-top-0.5 tw-h-5 tw-w-5 tw-rounded-full tw-bg-white tw-shadow-sm tw-transition-[left] tw-duration-200 ${granted ? 'tw-left-[1.375rem]' : 'tw-left-0.5'}`}
                      />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
};

CargoAccessModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
};

export default CargoAccessModal;
